import React from 'react';
import { alpha, Box, Button, FormControl, IconButton, InputAdornment, Menu, MenuItem, Select, SelectChangeEvent, Stack, TextField, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { IContentLibraryFiltersProps } from './types';
import { VideoIcon } from '../../../../../../../icons/VideoIcon';
import { AudioIcon } from '../../../../../../../icons/AudioIcon';
import { TextIcon } from '../../../../../../../icons/TextIcon';
import { Tabs } from '../../../../../../common/components/Tabs/Tabs';
import { Tab } from '../../../../../../common/components/Tab/Tab';
import { SearchIcon } from '../../../../../../../icons/SearchIcon';
import { DeviceContext } from '../../../../../../common/contexts/DeviceContext';
import { CloseIconLight } from '../../../../../../../icons/CloseIconLight';
import { EUserLevel } from '../../../../../store/general/types';
import { PlayListLevelTitles } from '../../../PlayList/PlayListLevelSelect/types';
import { LevelsIcon } from '../../../../../../../icons/LevelsIcon';
import { ArrowMuiIcon } from '../../../../../../../icons/ArrowMuiIcon';

export function ContentLibraryFilters({
	text,
	level,
	isVideoList,
	isOpenCategories,
	isCategoryId,
	onChangeText,
	onLevelChange,
	onOpenCategories,
}: IContentLibraryFiltersProps) {

	const className = 'content-library-filters';
	const CLASS_CONTROLS = 'content-library-filters-text-controls';

	const {isMobile, isSmall} = React.useContext(DeviceContext);

	const [searchText, setSearchText] = React.useState<string>('');
	const [searchChangedText, setSearchChangedText] = React.useState<string>('');

	const onSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target?.value || '');
	}

	const onSearchTextClear = () => {
		if (!!text) {
			setSearchChangedText('');
		} else {
			setSearchText('');
		}
	}

	const onSearchBlur = (e: React.FocusEvent) => {
		const target = e.relatedTarget as HTMLElement;
		const controls = target?.closest(`.${CLASS_CONTROLS}`);
		if (controls) return;
		
		setSearchChangedText(searchText);
	}

	const onSearchKeyPress = (event: React.KeyboardEvent) => {
		if ('Enter' === event.key) {
			setSearchChangedText(searchText);
		}
	}

	React.useEffect(() => {
		setSearchText(text);
	}, [text])

	React.useEffect(() => {
		onChangeText(searchChangedText);
	}, [searchChangedText])

	const onChangeLevelSelect = (event: SelectChangeEvent) => {
		onLevelChange(+event.target.value);
	};

	const onChangeOpenCategories = () => {
		onOpenCategories(!isOpenCategories);
	};

	const levels: EUserLevel[] = (Object.keys(PlayListLevelTitles) as any[]);

	return (
		<Stack
			className={`${className}__stack`}
			direction={'row'}
			alignItems={'center'}
			gap={2}
			sx={{
				width: 1,
				height: 1,
				ml: '3vw',
			}}
		>
			{isVideoList &&
				<Button
					className={`${isOpenCategories ? 'opened' : ''}
						${isCategoryId ? 'active' : ''}`}
					onClick={onChangeOpenCategories}
					sx={{
						height: theme.spacing(5),
						p: theme.spacing(1),
						fontWeight: 500,
						color: theme.palette.grey[400],
						backgroundColor: alpha(theme.palette.common.white, .5),
						borderRadius: theme.spacing(.5),
						transition: 'all .3s ease',

						'&:hover, &:focus, &.opened': {
							color: theme.palette.grey[600],
							backgroundColor: theme.palette.common.white,
						},

						'&.opened .MuiSvgIcon-root': {
							transform: 'scaleY(-1)',
						},

						'&.active': {
							color: theme.palette.common.white,
							backgroundColor: theme.palette.primary.main,

							'.MuiSvgIcon-root': {
								color: theme.palette.common.white,
							},
						},
					}}
				>
					Categories
					<ArrowMuiIcon
						sx={{
							width: theme.spacing(3),
							height: theme.spacing(3),
							ml: theme.spacing(.5),
							color: theme.palette.primary.main,
						}}
					/>
				</Button>
			}
			{isVideoList &&
				<FormControl
					className={`${!!level ? 'active' : ''}`}
					sx={{
						minWidth: theme.spacing(16),

						'.MuiInputBase-root': {
							backgroundColor: alpha(theme.palette.common.white, .5),
							borderRadius: theme.spacing(.5),
							transition: 'all .3s ease',

							'&:hover, &.Mui-focused': {
								backgroundColor: theme.palette.common.white,
							},

							'& > .MuiSvgIcon-root': {
								mt: theme.spacing(-.25),
							},
						},

						'&.active': {
							'.MuiInputBase-root': {
								color: theme.palette.common.white,
								backgroundColor: theme.palette.primary.main,
							},

							'.MuiSvgIcon-root': {
								color: theme.palette.common.white,
							},
						},
					}}
				>
					<Select
						value={String(level)}
						onChange={onChangeLevelSelect}
						variant={'filled'}
						renderValue={(level) => {
							if (!+level) {
								return <Stack
									flexDirection={'row'}
									gap={theme.spacing(1)}
									sx={{
										color: theme.palette.grey[400],
										fontWeight: 500,
									}}
								>
									<LevelsIcon
										sx={{
											width: theme.spacing(2),
											height: theme.spacing(2),
										}}
									/> Levels
								</Stack>
							}
				
							return PlayListLevelTitles[level];
						}}
						fullWidth
						MenuProps={{
							PaperProps: {
								style: {
									minWidth: theme.spacing(16),
									borderRadius: theme.spacing(.5),
								},
							},
						}}
						sx={{
							fontWeight: 500,
						}}
					>
						{
							levels.map((level) => {
								return (
									<MenuItem
										key={level}
										value={level}
									>
										{level == EUserLevel.UNKNOWN ? 'All' : PlayListLevelTitles[level]}
									</MenuItem>
								);
							})
						}
					</Select>
				</FormControl>
			}

			<Tabs
				className={`${className}__tabs`}
				sx={{
					p: 0,
					overflow: 'visible',

					'.MuiTabs-flexContainer': {
						gap: theme.spacing(.75),
					},

					'.MuiTab-root': {
						minWidth: theme.spacing(5),
						minHeight: theme.spacing(5),
						padding: theme.spacing(0, 1),
						fontSize: theme.typography.subtitle2,
						cursor: 'default', // while audio & text disabled

						'.MuiSvgIcon-root': {
							opacity: .3,
						},

						'&.Mui-selected': {
							'.MuiSvgIcon-root': {
								color: theme.palette.primary.main,
								opacity: .9,
							}
						}
					},

					'@media (max-width: 600px)': {
						display: 'none',
					}
				}}
				value={0}
				onChange={(e, value) => {}}
			>
				<Tooltip
					title={'Videos'}
				>
					<Tab
						icon={<VideoIcon />}
						iconPosition={'start'}	
					/>
				</Tooltip>
				<Tooltip
					title={'Audiobooks'}
				>
					<Tab
						icon={<AudioIcon />}
						iconPosition={'start'}
					/>
				</Tooltip>
				<Tooltip
					title={'Text'}
				>
					<Tab
						icon={<TextIcon />}
						iconPosition={'start'}
					/>
				</Tooltip>
			</Tabs>

			<Box
				sx={{
					flex: `0 1 ${theme.spacing(50)}`,
					position: 'relative',
				}}
			>
				<TextField
					sx={{
						width: 1,

						'.MuiInputBase-root': {
							height: theme.spacing(5),
							bgcolor: `${theme.palette.common.white} !important`,
						},

						'.MuiInputAdornment-root': {
							opacity: .3,
						},
					}}
					variant={'filled'}
					hiddenLabel
					InputProps={{
						startAdornment: (
							<InputAdornment position={'start'}>
								<SearchIcon />
							</InputAdornment>
						),
					}}
					placeholder={'Search...'}
					value={searchText}
					onBlur={onSearchBlur}
					onKeyPress={onSearchKeyPress}
					onChange={onSearchTextChange}
				/>

				{searchText &&
					<IconButton
						className={CLASS_CONTROLS}
						onClick={onSearchTextClear}
						color={'primary'}
						sx={{
							position: 'absolute',
							top: theme.spacing(.75),
							right: theme.spacing(1),
							color: theme.palette.grey[400],

							'&:hover, &:active, &:focus': {
								color: theme.palette.grey[300],
							},
						}}
					>
						<CloseIconLight />
					</IconButton>
				}
			</Box>

		</Stack>
	);
};
